import { Popover, PopoverAnchor, PopoverContent } from '@newfront-insurance/core-ui/v2';
import { useCallback, useState } from 'react';

import type { PromptCommand } from '../../../../lib/slash-command-constants';
import { SlashCommandList } from '../slash-command';

interface SlashCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (command: PromptCommand) => void;
  commands: PromptCommand[];
  searchQuery: string;
  anchorRef: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
}

/**
 * Popover-based slash command menu for desktop/wide panels.
 * Uses the shared SlashCommandList with 'default' variant.
 */
export function SlashCommandMenu({
  isOpen,
  onClose,
  onSelect,
  commands,
  searchQuery,
  anchorRef,
}: SlashCommandMenuProps) {
  if (!isOpen || commands.length === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <span className="pointer-events-none absolute bottom-full left-0" />
      </PopoverAnchor>
      <PopoverContent
        className="border-steel-200 w-[548px] overflow-hidden rounded-sm border bg-white p-0 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)]"
        side="top"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SlashCommandList
          commands={commands}
          searchQuery={searchQuery}
          isOpen={isOpen}
          onSelect={onSelect}
          onClose={onClose}
          inputRef={anchorRef}
          variant="default"
          showKeyboardHints
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Hook for managing slash command menu state.
 * Use this with SlashCommandMenu or SlashCommandList.
 */
export function useSlashCommandMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const open = useCallback((initialQuery = '/') => {
    setSearchQuery(initialQuery);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  const updateQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    isOpen,
    searchQuery,
    open,
    close,
    updateQuery,
  };
}
